import React, { useState, useEffect } from 'react';
import { Download, Calendar, Mail, User, FileText, Search, ArrowDown, ArrowUp, Loader, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface PromptDownload {
  id: string;
  prompt_id: string;
  name: string;
  email: string;
  created_at: string;
  source: string;
  prompt: {
    title: string;
  };
}

export default function AdminPromptDownloads() {
  const [downloads, setDownloads] = useState<PromptDownload[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchDownloads();
  }, [sortField, sortDirection]);

  const fetchDownloads = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error } = await supabase
        .from('prompt_downloads')
        .select(`
          *,
          prompt:prompts(title)
        `)
        .order(sortField, { ascending: sortDirection === 'asc' });

      if (error) throw error;
      setDownloads(data || []);
    } catch (err) {
      console.error('Error fetching prompt downloads:', err);
      setError('Failed to load download data');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: string) => {
    if (field === sortField) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const filteredDownloads = downloads.filter(download => {
    if (!searchQuery) return true;
    
    const searchLower = searchQuery.toLowerCase();
    return (
      (download.name && download.name.toLowerCase().includes(searchLower)) ||
      download.email.toLowerCase().includes(searchLower) ||
      (download.prompt?.title && download.prompt.title.toLowerCase().includes(searchLower)) ||
      (download.source && download.source.toLowerCase().includes(searchLower))
    );
  });

  const exportCSV = () => {
    // Create CSV content
    const headers = ['Name', 'Email', 'Prompt', 'Source', 'Date'];
    const rows = filteredDownloads.map(download => [
      download.name || '',
      download.email,
      download.prompt?.title || '',
      download.source || '',
      new Date(download.created_at).toLocaleString()
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    // Create and download the file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `prompt-downloads-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SortIcon = ({ field }: { field: string }) => {
    if (field !== sortField) return null;
    return sortDirection === 'asc' ? 
      <ArrowUp className="h-4 w-4 ml-1" /> : 
      <ArrowDown className="h-4 w-4 ml-1" />;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader className="h-8 w-8 text-[#F98B3D] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded-md">
        <div className="flex">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Prompt Downloads</h1>
        <button
          onClick={exportCSV}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#F98B3D] hover:bg-[#e07a2c]"
        >
          <Download className="h-5 w-5 mr-2" />
          Export CSV
        </button>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name, email, prompt, or source..."
            className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#F98B3D] focus:border-[#F98B3D]"
          />
        </div>
      </div>

      {filteredDownloads.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Downloads Found</h3>
          <p className="text-gray-500">
            {searchQuery ? 'No downloads match your search criteria.' : 'No prompt downloads have been recorded yet.'}
          </p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      <User className="h-4 w-4 mr-1" />
                      Name
                      <SortIcon field="name" />
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      <Mail className="h-4 w-4 mr-1" />
                      Email
                      <SortIcon field="email" />
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('prompt_id')}
                  >
                    <div className="flex items-center">
                      <FileText className="h-4 w-4 mr-1" />
                      Prompt
                      <SortIcon field="prompt_id" />
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      <Calendar className="h-4 w-4 mr-1" />
                      Date
                      <SortIcon field="created_at" />
                    </div>
                  </th>
                  <th 
                    scope="col" 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                    onClick={() => handleSort('source')}
                  >
                    <div className="flex items-center">
                      <Download className="h-4 w-4 mr-1" />
                      Source
                      <SortIcon field="source" />
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDownloads.map((download) => (
                  <tr key={download.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {download.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <a href={`mailto:${download.email}`} className="text-[#F98B3D] hover:underline">
                        {download.email}
                      </a>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {download.prompt?.title || 'Unknown Prompt'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(download.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {download.source || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}